import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRBAC } from '../hooks/useRBAC';
import { useUserStore } from '../stores/useUserStore';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Plus, Search, Edit, Trash2, ChevronRight, ChevronDown, Grid3x3, ListTree } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { productsService } from '../services/productsService';
import { ProductCategory } from 'public-contracts';

const ModernProductCategoryList: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useRBAC();
  const { user, fetchUser } = useUserStore();

  // Permission checks
  const canCreate = hasPermission('product_categories', 'create');
  const canEdit = hasPermission('product_categories', 'edit');
  const canDelete = hasPermission('product_categories', 'delete');

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [allCategories, setAllCategories] = useState<ProductCategory[]>([]); // For tree view
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Load view mode from localStorage, default to 'grid'
  const [viewMode, setViewMode] = useState<'grid' | 'tree'>(() => {
    const saved = localStorage.getItem('productCategoryViewMode');
    return (saved === 'grid' || saved === 'tree') ? saved : 'grid';
  });
  
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('productCategoryViewMode', viewMode);
  }, [viewMode]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
  });

  // Ensure user data is loaded
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // Load all categories for tree view
  useEffect(() => {
    if (viewMode === 'tree') {
      const loadAllCategories = async () => {
        try {
          const categoriesData = await productsService.getProductCategories({
            page: 1,
            perPage: 1000, // Load all for tree view
            ...(searchTerm && {
              filter: { name: { $regex: searchTerm, $options: 'i' } },
            }),
          });

          const categoriesArray = Array.isArray(categoriesData)
            ? categoriesData
            : [];

          setAllCategories(categoriesArray);
          // Auto-expand all nodes when switching to tree view
          setExpandedNodes(new Set(categoriesArray.map((cat) => cat.id)));
        } catch (error) {
          console.error('Error loading all categories:', error);
        }
      };

      loadAllCategories();
    }
  }, [viewMode, searchTerm]);

  // Load real data - reload when location changes or pagination changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const categoriesData = await productsService.getProductCategories({
          page: pagination.page,
          perPage: pagination.perPage,
          ...(searchTerm && {
            filter: { name: { $regex: searchTerm, $options: 'i' } },
          }),
        });

        // Ensure we have an array
        const categoriesArray = Array.isArray(categoriesData)
          ? categoriesData
          : [];

        const total = (categoriesData as any).__total || categoriesArray.length;

        console.log('Product categories data:', categoriesArray);
        console.log(`✅ Loaded ${categoriesArray.length} categories (page ${pagination.page}, total: ${total})`);

        setCategories(categoriesArray);
        setPagination((prev) => ({ ...prev, total }));
        setError(null); // Clear any previous errors
      } catch (error) {
        console.error('Error loading product category data:', error);
        setError(translate('pos.product_categories.load_error'));
        // Set empty array as fallback
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    if (viewMode === 'grid') {
    loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, pagination.page, pagination.perPage, viewMode]);

  // Debounce search and reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
      } else {
        // Trigger reload if already on page 1
        const loadData = async () => {
          try {
            setLoading(true);
            const categoriesData = await productsService.getProductCategories({
              page: 1,
              perPage: pagination.perPage,
              ...(searchTerm && {
                filter: { name: { $regex: searchTerm, $options: 'i' } },
              }),
            });

            const categoriesArray = Array.isArray(categoriesData)
              ? categoriesData
              : [];
            const total = (categoriesData as any).__total || categoriesArray.length;

            setCategories(categoriesArray);
            setPagination((prev) => ({ ...prev, total }));
          } catch (error) {
            console.error('Error loading product category data:', error);
            setCategories([]);
          } finally {
            setLoading(false);
          }
        };
        loadData();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, pagination.perPage]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // Build tree structure from flat category list
  const categoryTree = useMemo(() => {
    if (viewMode !== 'tree') return [];

    const categoryMap = new Map<string, ProductCategory & { children: ProductCategory[] }>();
    const rootCategories: (ProductCategory & { children: ProductCategory[] })[] = [];

    // First pass: create map with children arrays
    allCategories.forEach((category) => {
      categoryMap.set(category.id, { ...category, children: [] });
    });

    // Second pass: build tree structure
    allCategories.forEach((category) => {
      const categoryWithChildren = categoryMap.get(category.id)!;
      if (category.parentId && categoryMap.has(category.parentId)) {
        const parent = categoryMap.get(category.parentId)!;
        parent.children.push(categoryWithChildren);
      } else {
        rootCategories.push(categoryWithChildren);
      }
    });

    // Sort by level and name
    const sortCategories = (
      cats: (ProductCategory & { children: ProductCategory[] })[]
    ) => {
      cats.sort((a, b) => {
        if (a.level !== undefined && b.level !== undefined) {
          if (a.level !== b.level) return a.level - b.level;
        }
        return a.name.localeCompare(b.name);
      });
      cats.forEach((cat) => {
        if (cat.children.length > 0) {
          sortCategories(cat.children as (ProductCategory & { children: ProductCategory[] })[]);
        }
      });
    };

    sortCategories(rootCategories);
    return rootCategories;
  }, [allCategories, viewMode]);

  // Toggle node expansion
  const toggleNode = (categoryId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // No client-side filtering needed - server handles it via API filter
  const displayedCategories = categories;

  const handleCreateCategory = () => {
    navigate('/product_categories/create');
  };

  const handleEditCategory = (id: string) => {
    navigate(`/product_categories/${id}/edit`);
  };

  const handleDeleteCategory = async (id: string) => {
    if (
      window.confirm(
        translate('pos.product_categories.delete_confirm')
      )
    ) {
      try {
        await productsService.deleteProductCategory(id);
        setCategories(categories.filter((cat) => cat.id !== id));
      } catch (error) {
        console.error('Error deleting category:', error);
        alert(translate('pos.product_categories.delete_error'));
      }
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          {translate('pos.product_categories.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {translate('pos.product_categories.title')}
          </h1>
          <p className="text-muted-foreground">
            {translate('pos.product_categories.subtitle')}
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleCreateCategory}>
            <Plus className="h-4 w-4 mr-2" />
            {translate('pos.product_categories.new_category')}
          </Button>
        )}
      </div>

      {/* Search and View Toggle */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={translate(
                'pos.product_categories.search_placeholder'
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            </div>
            <div className="flex items-center gap-2 border rounded-md p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="gap-2"
              >
                <Grid3x3 className="h-4 w-4" />
                {translate('pos.product_categories.view_grid', 'Grid')}
              </Button>
              <Button
                variant={viewMode === 'tree' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('tree')}
                className="gap-2"
              >
                <ListTree className="h-4 w-4" />
                {translate('pos.product_categories.view_tree', 'Tree')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-red-500">
          <CardContent className="pt-6">
            <div className="text-red-500">{error}</div>
          </CardContent>
        </Card>
      )}

      {/* Categories Grid View */}
      {viewMode === 'grid' && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayedCategories.map((category) => (
          <Card key={category.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">{category.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(canEdit || canDelete) && (
                <div className="flex gap-2 pt-2">
                  {canEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditCategory(category.id)}
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      {translate('pos.common.action.edit')}
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteCategory(category.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {/* Categories Tree View */}
      {viewMode === 'tree' && (
        <Card>
          <CardContent className="pt-6">
            {categoryTree.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm
                  ? translate('pos.product_categories.no_results')
                  : translate('pos.product_categories.no_categories')}
              </div>
            ) : (
              <div className="space-y-1">
                {categoryTree.map((category) => (
                  <CategoryTreeNode
                    key={category.id}
                    category={category}
                    level={0}
                    expandedNodes={expandedNodes}
                    onToggle={toggleNode}
                    onEdit={canEdit ? handleEditCategory : undefined}
                    onDelete={canDelete ? handleDeleteCategory : undefined}
                    translate={translate}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {viewMode === 'grid' && displayedCategories.length === 0 && !loading && (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            {searchTerm
              ? translate('pos.product_categories.no_results')
              : translate('pos.product_categories.no_categories')}
          </CardContent>
        </Card>
      )}

      {/* Pagination - Only show in grid view */}
      {viewMode === 'grid' && pagination.total > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-20">
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 gap-4">
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              {translate('pos.orders.modern.pagination.showing', {
                start: Math.min(
                  (pagination.page - 1) * pagination.perPage + 1,
                  pagination.total
                ),
                end: Math.min(
                  pagination.page * pagination.perPage,
                  pagination.total
                ),
                total: pagination.total,
              })}{' '}
              {translate(
                'pos.product_categories.name',
                'categories'
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.previous',
                  'Previous'
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={
                  pagination.page * pagination.perPage >= pagination.total
                }
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.next',
                  'Next'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tree Node Component
interface CategoryTreeNodeProps {
  category: ProductCategory & { children: ProductCategory[] };
  level: number;
  expandedNodes: Set<string>;
  onToggle: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  translate: (key: string, fallback?: string) => string;
}

const CategoryTreeNode: React.FC<CategoryTreeNodeProps> = ({
  category,
  level,
  expandedNodes,
  onToggle,
  onEdit,
  onDelete,
  translate,
}) => {
  const hasChildren = category.children.length > 0;
  const isExpanded = expandedNodes.has(category.id);
  const indent = level * 24;

  return (
    <div>
      <div
        className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors group"
        style={{ paddingLeft: `${indent + 8}px` }}
      >
        {hasChildren ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => onToggle(category.id)}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <div className="w-6" /> // Spacer for alignment
        )}
        <div className="flex-1 font-medium">{category.name}</div>
        {category.level !== undefined && category.level > 0 && (
          <Badge variant="outline" className="text-xs">
            L{category.level}
          </Badge>
        )}
        {(onEdit || onDelete) && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {onEdit && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onEdit(category.id)}
              >
                <Edit className="h-3 w-3" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                onClick={() => onDelete(category.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {category.children.map((child) => (
            <CategoryTreeNode
              key={child.id}
              category={child as ProductCategory & { children: ProductCategory[] }}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              translate={translate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ModernProductCategoryList;
